import { useState, useEffect, useRef } from 'react';
import { FlagImg } from '@/components/ui/CountryFlag';
import { useOnboarding } from '../OnboardingContext';
import { AlertCircle, ChevronDown, Search, Check, Pencil } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { countriesSorted, countries } from '@/data/countries';

const INVALID_VALS = ['NotSet', 'None', 'Unknown', 'N/A', 'n/a', 'NA', 'na', 'null', 'undefined'];
const sanitize = (v: string | undefined | null) => (!v || INVALID_VALS.includes(v.trim())) ? '' : v.trim();
const parseToISO = (v: string | undefined | null): string => {
  if (!v || INVALID_VALS.includes((v || '').trim())) return '';
  const d = new Date(v);
  return d.toString() === 'Invalid Date' ? '' : d.toISOString().split('T')[0];
};

// Inline editable row — shows full value, wraps on long text, taps to edit
function EditableRow({
  label, value, fieldKey, editingField, setEditingField, onChange, readOnly = false, type = 'text'
}: {
  label: string; value: string; fieldKey: string; editingField: string | null;
  setEditingField: (f: string | null) => void; onChange: (v: string) => void;
  readOnly?: boolean; type?: string;
}) {
  const isEditing = editingField === fieldKey && !readOnly;
  const empty = !value;

  return (
    <div
      className="flex items-start justify-between py-3 border-b border-border/30 last:border-0 gap-3"
      onClick={() => !readOnly && !isEditing && setEditingField(fieldKey)}
    >
      <span className="text-xs text-muted-foreground shrink-0 w-24 uppercase tracking-wide pt-0.5">{label}</span>
      {isEditing ? (
        <Input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setEditingField(null)}
          onKeyDown={(e) => e.key === 'Enter' && setEditingField(null)}
          className="flex-1 h-8 text-right text-sm font-medium border-0 border-b border-primary rounded-none bg-transparent px-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          autoFocus
        />
      ) : (
        <div className="flex items-start gap-1.5 flex-1 justify-end min-w-0">
          <span className={`text-sm font-medium text-right break-words whitespace-pre-wrap min-w-0 flex-1 ${empty ? 'text-accent italic' : 'text-foreground'}`}>
            {empty
              ? (readOnly ? '—' : 'Tap to add')
              : type === 'date'
                ? new Date(value + 'T00:00:00').toLocaleDateString('en-GB')
                : value
            }
          </span>
          {!readOnly && (
            <Pencil className={`w-3 h-3 shrink-0 mt-0.5 ${empty ? 'text-accent' : 'text-muted-foreground/40'}`} />
          )}
        </div>
      )}
    </div>
  );
}

// Toggle switch styled for fintech
function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-10 shrink-0 rounded-full transition-colors duration-200 focus:outline-none ${
        checked ? 'bg-primary' : 'bg-muted-foreground/30'
      }`}
    >
      <span className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
        checked ? 'translate-x-4' : 'translate-x-0.5'
      }`} />
    </button>
  );
}

// Expandable SIC/NACE codes card
function SicCodesCard({ sicCodes }: { sicCodes: Array<{ code: string; description: string }> }) {
  const [expanded, setExpanded] = useState(false);
  const PREVIEW_COUNT = 2;
  const visible = expanded ? sicCodes : sicCodes.slice(0, PREVIEW_COUNT);
  const hasMore = sicCodes.length > PREVIEW_COUNT;

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      <div className="bg-muted/40 border-b border-border/50 px-4 py-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Industry (SIC/NACE)</span>
        {hasMore && (
          <button type="button" onClick={() => setExpanded(e => !e)}
            className="flex items-center gap-1 text-[11px] text-primary/70 hover:text-primary transition-colors">
            {expanded ? 'Show less' : `+${sicCodes.length - PREVIEW_COUNT} more`}
            <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>
      <div className="divide-y divide-border/30">
        {visible.map((sic, i) => (
          <div key={i} className="flex items-start gap-3 px-4 py-2.5">
            <span className="shrink-0 font-mono text-xs font-bold text-primary bg-primary/10 rounded px-1.5 py-0.5 mt-0.5">{sic.code}</span>
            <span className="text-sm text-foreground/80 leading-snug">{sic.description}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CorporateCompanyDetailsStep() {
  const { data, updateData, goToNextStep, setCurrentStep, setCustomBackHandler } = useOnboarding();
  const company = data.corporateCompany;

  const getCountryName = () => {
    if (company?.fullAddress?.country) return company.fullAddress.country;
    if (company?.countryCode) return countries.find(c => c.code === company.countryCode)?.name || '';
    return '';
  };

  const [registrationNumber, setRegistrationNumber] = useState(company?.registrationNumber || '');
  const [companyName, setCompanyName] = useState(company?.name || '');
  const [companyType, setCompanyType] = useState(sanitize(company?.companyType));
  const [incorporationDate, setIncorporationDate] = useState(parseToISO(company?.incorporationDate));
  const [street, setStreet] = useState(company?.fullAddress?.street || '');
  const [city, setCity] = useState(company?.fullAddress?.city || '');
  const [postalCode, setPostalCode] = useState(company?.fullAddress?.postalCode || '');
  const [country, setCountry] = useState(getCountryName());

  // VAT — auto-tick if API returned a VAT number
  const apiVatNumber = company?.vatNumber || '';
  const vatFromApi = apiVatNumber.length > 0;
  const vatMatchesReg = vatFromApi && apiVatNumber === (company?.registrationNumber || '');

  const [vatRegistered, setVatRegistered] = useState(company?.vatRegistered === true || vatFromApi);
  const [vatSameAsReg, setVatSameAsReg] = useState(
    company?.vatSameAsRegistration !== undefined ? company.vatSameAsRegistration : vatMatchesReg
  );
  const [vatNumber, setVatNumber] = useState(vatMatchesReg ? (company?.registrationNumber || '') : apiVatNumber);

  // UTR / Tax — same pattern
  const apiTaxNumber = company?.taxNumber || '';
  const taxFromApi = apiTaxNumber.length > 0;
  const taxMatchesReg = taxFromApi && apiTaxNumber === (company?.registrationNumber || '');
  const [taxSameAsReg, setTaxSameAsReg] = useState(
    company?.taxNumberSameAsRegistration !== undefined ? company.taxNumberSameAsRegistration : (!taxFromApi || taxMatchesReg)
  );
  const [taxNumber, setTaxNumber] = useState(taxMatchesReg ? (company?.registrationNumber || '') : apiTaxNumber);

  const [editingField, setEditingField] = useState<string | null>(null);
  const [isSameAsTradingAddress, setIsSameAsTradingAddress] = useState(true);
  const [tradingAddress, setTradingAddress] = useState({ street: '', city: '', postalCode: '', country: 'United Kingdom' });
  const [showTradingCountryPicker, setShowTradingCountryPicker] = useState(false);
  const [tradingCountrySearch, setTradingCountrySearch] = useState('');
  const tradingCountryPickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = () => { setCurrentStep('corporate-company-search'); return true; };
    setCustomBackHandler(handler);
    return () => setCustomBackHandler(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (company) {
      setRegistrationNumber(company.registrationNumber || '');
      setCompanyName(company.name || '');
      setCompanyType(sanitize(company.companyType));
      setIncorporationDate(parseToISO(company.incorporationDate));
      setStreet(company.fullAddress?.street || '');
      setCity(company.fullAddress?.city || '');
      setPostalCode(company.fullAddress?.postalCode || '');
      setCountry(company.fullAddress?.country || countries.find(c => c.code === company.countryCode)?.name || '');
    }
  }, [company]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tradingCountryPickerRef.current && !tradingCountryPickerRef.current.contains(e.target as Node))
        setShowTradingCountryPicker(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!company) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 pt-24">
      <div className="text-center">
        <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
        <p className="text-muted-foreground">Company data not found. Please go back and search again.</p>
      </div>
    </div>
  );

  const filteredTradingCountries = countriesSorted.filter(c =>
    c.name.toLowerCase().includes(tradingCountrySearch.toLowerCase())
  );
  const selectedTradingCountryData = countriesSorted.find(c => c.name === tradingAddress.country);

  const handleContinue = () => {
    updateData({
      corporateCompany: {
        ...company,
        name: companyName,
        companyType,
        incorporationDate,
        fullAddress: { street, city, postalCode, country },
        registeredAddress: `${street}, ${city}, ${postalCode}, ${country}`,
        vatRegistered,
        vatSameAsRegistration: vatRegistered ? vatSameAsReg : undefined,
        vatNumber: vatRegistered ? vatNumber : undefined,
        taxNumber: taxSameAsReg ? registrationNumber : taxNumber,
        taxNumberSameAsRegistration: taxSameAsReg,
      },
      tradingAddress: isSameAsTradingAddress ? undefined : tradingAddress
    });
    goToNextStep();
  };

  const isFormValid = () => {
    const coreValid = !!(companyName.trim() && street.trim() && city.trim() && postalCode.trim());
    const vatValid = !vatRegistered || (vatSameAsReg ? true : vatNumber.trim().length > 0);
    const taxValid = taxSameAsReg || taxNumber.trim().length > 0;
    if (isSameAsTradingAddress) return coreValid && vatValid && taxValid;
    const tradingValid = !!(tradingAddress.street.trim() && tradingAddress.city.trim() && tradingAddress.postalCode.trim());
    return coreValid && tradingValid && vatValid && taxValid;
  };

  const statusColor = (s: string) => {
    const sl = (s || '').toLowerCase();
    if (sl === 'active') return 'text-green-400';
    if (sl === 'dissolved' || sl === 'inactive') return 'text-destructive';
    return 'text-yellow-400';
  };

  return (
    <div className="min-h-screen bg-background flex flex-col pt-16 pb-28 px-4">
      <div className="max-w-md mx-auto w-full space-y-3 animate-fade-in-up">

        {/* Header */}
        <div className="text-center pb-1">
          <h1 className="text-lg font-bold tracking-[0.18em] text-foreground">COMPANY DETAILS</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Verify the information below — tap any field to correct</p>
        </div>

        {/* Identity card */}
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          {/* Card header strip */}
          <div className="bg-primary/10 border-b border-border/50 px-4 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-primary uppercase tracking-widest">Company Registration</span>
            <span className={`text-xs font-bold uppercase ${statusColor(company?.status || 'Active')}`}>
              ● {company?.status || 'Active'}
            </span>
          </div>
          <div className="px-4 py-1">
            <EditableRow label="Name" value={companyName} fieldKey="name" editingField={editingField} setEditingField={setEditingField} onChange={setCompanyName} />
            <EditableRow label="Reg. No." value={registrationNumber} fieldKey="regNo" editingField={editingField} setEditingField={setEditingField} onChange={setRegistrationNumber} />
            <EditableRow label="Type" value={companyType} fieldKey="type" editingField={editingField} setEditingField={setEditingField} onChange={setCompanyType} />
            <EditableRow label="Incorporated" value={incorporationDate} fieldKey="incDate" editingField={editingField} setEditingField={setEditingField} onChange={setIncorporationDate} type="date" />
          </div>
        </div>

        {/* Address card */}
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="bg-muted/40 border-b border-border/50 px-4 py-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Registered Address</span>
          </div>
          <div className="px-4 py-1">
            <EditableRow label="Street" value={street} fieldKey="street" editingField={editingField} setEditingField={setEditingField} onChange={setStreet} />
            <EditableRow label="City" value={city} fieldKey="city" editingField={editingField} setEditingField={setEditingField} onChange={setCity} />
            <EditableRow label="Post Code" value={postalCode} fieldKey="postalCode" editingField={editingField} setEditingField={setEditingField} onChange={setPostalCode} />
            <EditableRow label="Country" value={country} fieldKey="country" editingField={editingField} setEditingField={setEditingField} onChange={setCountry} readOnly />
          </div>
        </div>

        {/* SIC codes — expandable list */}
        {company?.sicCodes && company.sicCodes.length > 0 && (
          <SicCodesCard sicCodes={company.sicCodes} />
        )}

        {/* VAT & Tax card */}
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="bg-muted/40 border-b border-border/50 px-4 py-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Tax & VAT</span>
          </div>

          {/* VAT row */}
          <div className="px-4 py-3 border-b border-border/30">
            <div className="flex items-center gap-3">
              <ToggleSwitch checked={vatRegistered} onChange={(v) => {
                setVatRegistered(v);
                if (!v) { setVatSameAsReg(false); }
              }} />
              <span className="text-sm text-foreground flex-1">VAT registered</span>
              {vatRegistered && (
                vatSameAsReg ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-mono text-foreground/80">{registrationNumber}</span>
                    <button type="button" onClick={() => setVatSameAsReg(false)} className="text-[11px] text-primary/70 hover:text-primary transition-colors">edit</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 max-w-[55%]">
                    <Input
                      value={vatNumber}
                      onChange={(e) => setVatNumber(e.target.value.toUpperCase())}
                      placeholder="VAT number"
                      className="h-7 text-xs font-mono text-right px-2 w-32"
                      inputMode="text"
                      autoCapitalize="characters"
                    />
                    <button type="button" onClick={() => { setVatSameAsReg(true); setVatNumber(registrationNumber); }}
                      className="text-[11px] text-primary/70 hover:text-primary transition-colors whitespace-nowrap">= Reg.</button>
                  </div>
                )
              )}
            </div>
          </div>

          {/* UTR row */}
          <div className="px-4 py-3">
            <div className="flex items-center gap-3">
              <ToggleSwitch checked={!taxSameAsReg} onChange={(v) => setTaxSameAsReg(!v)} />
              <span className="text-sm text-foreground flex-1">Different UTR / Tax ref</span>
              {taxSameAsReg ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-mono text-foreground/80">{registrationNumber}</span>
                  <button type="button" onClick={() => setTaxSameAsReg(false)} className="text-[11px] text-primary/70 hover:text-primary transition-colors">edit</button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 max-w-[55%]">
                  <Input
                    value={taxNumber}
                    onChange={(e) => setTaxNumber(e.target.value)}
                    placeholder="UTR / Tax ref"
                    className="h-7 text-xs font-mono text-right px-2 w-32"
                    inputMode="numeric"
                  />
                  <button type="button" onClick={() => { setTaxSameAsReg(true); setTaxNumber(registrationNumber); }}
                    className="text-[11px] text-primary/70 hover:text-primary transition-colors whitespace-nowrap">= Reg.</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Trading address card */}
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 flex items-center gap-3">
            <ToggleSwitch checked={isSameAsTradingAddress} onChange={setIsSameAsTradingAddress} />
            <div className="flex-1">
              <span className="text-sm text-foreground">Same trading address</span>
              <p className="text-xs text-muted-foreground">Registered address = trading address</p>
            </div>
          </div>

          {!isSameAsTradingAddress && (
            <div className="border-t border-border/50 px-4 pb-4 pt-3 space-y-3 animate-fade-in-up">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Trading Address</p>

              {/* Country selector */}
              <div className="relative" ref={tradingCountryPickerRef}>
                <button
                  type="button"
                  onClick={() => setShowTradingCountryPicker(!showTradingCountryPicker)}
                  className="w-full flex items-center justify-between text-left px-3 py-2.5 rounded-xl border border-border bg-background"
                >
                  <div className="flex items-center gap-2">
                    {selectedTradingCountryData && <FlagImg code={selectedTradingCountryData.code} size="sm" />}
                    <span className="text-sm text-foreground">{tradingAddress.country}</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showTradingCountryPicker ? 'rotate-180' : ''}`} />
                </button>
                {showTradingCountryPicker && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-card rounded-xl shadow-xl border border-border z-30 overflow-hidden">
                    <div className="p-2 border-b border-border">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <input
                          type="text"
                          placeholder="Search country..."
                          value={tradingCountrySearch}
                          onChange={(e) => setTradingCountrySearch(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-secondary border-none focus:outline-none"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="max-h-44 overflow-y-auto">
                      {filteredTradingCountries.map((c) => (
                        <button key={c.code} onClick={() => { setTradingAddress(p => ({ ...p, country: c.name })); setShowTradingCountryPicker(false); setTradingCountrySearch(''); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-secondary transition-colors text-left">
                          <FlagImg code={c.code} size="sm" />
                          <span className="text-sm flex-1">{c.name}</span>
                          {tradingAddress.country === c.name && <Check className="w-4 h-4 text-primary" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Input value={tradingAddress.street} onChange={(e) => setTradingAddress(p => ({ ...p, street: e.target.value }))}
                placeholder="Street address" className="h-11 text-sm" />
              <div className="flex gap-2">
                <Input value={tradingAddress.city} onChange={(e) => setTradingAddress(p => ({ ...p, city: e.target.value }))}
                  placeholder="City" className="h-11 text-sm flex-1" />
                <Input value={tradingAddress.postalCode} onChange={(e) => setTradingAddress(p => ({ ...p, postalCode: e.target.value }))}
                  placeholder="Post code" className="h-11 text-sm w-28" />
              </div>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground text-center pb-1">Tap any value to correct · Long-press to copy</p>
      </div>

      {/* Sticky Continue */}
      <div className="fixed bottom-0 left-0 right-0 px-4 pb-safe pt-3 bg-background/95 backdrop-blur-sm border-t border-border/50">
        <div className="max-w-md mx-auto">
          <button
            onClick={handleContinue}
            disabled={!isFormValid()}
            className="w-full h-14 rounded-2xl font-semibold text-base transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98]"
          >
            Confirm & Continue
          </button>
        </div>
      </div>
    </div>
  );
}
